<template>
  <div
    class="score-trend-chart"
    :class="{ 'is-ready': animReady, 'is-expanded': expanded }"
  >
    <h4 v-if="title" class="score-trend-title">{{ title }}</h4>

    <div class="score-trend-body">
      <span class="score-trend-y-label">{{ yAxisLabel }}</span>

      <div class="score-trend-plot-wrap">
        <svg
          class="score-trend-svg"
          :viewBox="`0 0 ${vbW} ${vbH}`"
          role="img"
          :aria-label="title || 'Score chart'"
          @mouseleave="clearHover"
        >
          <!-- Fail / Pass background bands -->
          <rect
            :x="plot.left"
            :y="plot.top"
            :width="failWidth"
            :height="plot.height"
            class="zone-fail"
          />
          <rect
            :x="passX"
            :y="plot.top"
            :width="passWidth"
            :height="plot.height"
            class="zone-pass"
          />

          <!-- Horizontal grid + row labels -->
          <g v-for="(row, i) in normalizedRows" :key="'row-' + row.key">
            <line
              :x1="plot.left"
              :y1="rowY(i)"
              :x2="plot.right"
              :y2="rowY(i)"
              class="grid-h"
            />
            <text
              :x="plot.left - 8"
              :y="rowY(i) + 3"
              class="axis-label axis-label--y"
              text-anchor="end"
            >{{ row.label }}</text>
          </g>

          <!-- Axes -->
          <line
            :x1="plot.left"
            :y1="plot.top"
            :x2="plot.left"
            :y2="plot.bottom"
            class="axis-line"
          />
          <line
            :x1="plot.left"
            :y1="plot.bottom"
            :x2="plot.right"
            :y2="plot.bottom"
            class="axis-line"
          />

          <!-- X tick marks + labels -->
          <g v-for="tick in scoreTicks" :key="'t-' + tick">
            <line
              :x1="scoreX(tick)"
              :y1="plot.bottom"
              :x2="scoreX(tick)"
              :y2="plot.bottom + 4"
              class="axis-tick"
            />
            <text
              :x="scoreX(tick)"
              :y="plot.bottom + 14"
              class="axis-label axis-label--x"
              text-anchor="middle"
            >{{ tick }}</text>
          </g>

          <!-- Passing score line (green dashed) -->
          <line
            :x1="animPassX"
            :y1="plot.top"
            :x2="animPassX"
            :y2="plot.bottom"
            class="ref-line ref-line--pass"
            :style="{ opacity: lineOpacity }"
          />

          <!-- Average user score line (black dashed) -->
          <line
            :x1="animAvgX"
            :y1="plot.top"
            :x2="animAvgX"
            :y2="plot.bottom"
            class="ref-line ref-line--avg"
            :style="{ opacity: lineOpacity }"
          />

          <!-- Score bars -->
          <g
            v-for="(bar, idx) in animatedBars"
            :key="'bar-' + bar.key"
            class="data-bar-group"
            @mouseenter="onBarEnter(bar, idx, $event)"
            @mousemove="onBarMove($event)"
            @mouseleave="clearHover"
          >
            <rect
              :x="plot.left"
              :y="bar.y - barHeight / 2"
              :width="bar.width"
              :height="barHeight"
              :rx="2"
              :ry="2"
              :fill="barColor"
              :opacity="bar.opacity"
              class="data-bar"
            />
            <!-- Invisible hit area for easier hover -->
            <rect
              :x="plot.left"
              :y="bar.y - Math.max(barHeight, 14) / 2"
              :width="plot.width"
              :height="Math.max(barHeight, 14)"
              fill="transparent"
              class="data-bar-hit"
            />
          </g>

          <!-- X axis title -->
          <text
            :x="(plot.left + plot.right) / 2"
            :y="vbH - 4"
            class="axis-title"
            text-anchor="middle"
          >{{ xAxisLabel }}</text>
        </svg>

        <div
          v-if="hoverTip"
          class="score-trend-tooltip"
          :style="{ left: hoverTip.x + 'px', top: hoverTip.y + 'px' }"
        >
          {{ hoverTip.text }}
        </div>
      </div>
    </div>

    <div class="score-trend-legend">
      <div class="legend-item legend-item--pass">
        <span class="legend-dash legend-dash--pass"></span>
        <span>Passing score in last exam {{ passingScore }}%</span>
      </div>
      <div class="legend-item legend-item--avg">
        <span class="legend-dash legend-dash--avg"></span>
        <span>Average user score {{ averageScore }}%</span>
      </div>
    </div>
  </div>
</template>

<script>
function easeOutCubic(t) {
  return 1 - Math.pow(1 - t, 3);
}

function clampScore(value) {
  const n = Number(value);
  if (!Number.isFinite(n) || n < 0) return 0;
  return Math.min(100, n);
}

export default {
  name: 'ScoreTrendChart',
  props: {
    title: {
      type: String,
      default: 'Scoring trend - Improvement in skill',
    },
    /**
     * Preferred row API:
     * [{ key?, label, score, tip? }]
     * tip is shown on hover when expanded (e.g. mock name).
     */
    rows: {
      type: Array,
      default() {
        return [];
      },
    },
    /** Legacy: week numbers used with `points` when `rows` is empty */
    weeks: {
      type: Array,
      default() {
        return [];
      },
    },
    /** Legacy: [{ week, score, tip?, label?, name? }] */
    points: {
      type: Array,
      default() {
        return [];
      },
    },
    barColor: {
      type: String,
      default: '#1a90ff',
    },
    passingScore: {
      type: Number,
      default: 70,
    },
    averageScore: {
      type: Number,
      default: 60,
    },
    expanded: {
      type: Boolean,
      default: false,
    },
    yAxisLabel: {
      type: String,
      default: 'Time',
    },
    xAxisLabel: {
      type: String,
      default: 'Score %',
    },
  },
  data() {
    return {
      vbW: 420,
      vbH: 220,
      plot: {
        left: 58,
        top: 10,
        right: 404,
        bottom: 188,
        width: 346,
        height: 178,
      },
      animProgress: 0,
      animReady: false,
      animFrame: null,
      animToken: 0,
      hoverTip: null,
    };
  },
  computed: {
    scoreTicks() {
      const ticks = [];
      for (let i = 0; i <= 100; i += 10) ticks.push(i);
      return ticks;
    },
    passX() {
      return this.scoreX(this.passingScore);
    },
    failWidth() {
      return Math.max(0, this.passX - this.plot.left);
    },
    passWidth() {
      return Math.max(0, this.plot.right - this.passX);
    },
    normalizedRows() {
      if (this.rows && this.rows.length) {
        return this.rows.map((row, idx) => {
          const score = clampScore(row.score);
          return {
            key: row.key != null ? row.key : `r-${idx}`,
            label: row.label || `Item ${idx + 1}`,
            score,
            tip: row.tip || row.name || '',
          };
        });
      }

      const weekList = (this.weeks && this.weeks.length)
        ? this.weeks
        : (this.points || []).map((p) => p.week).filter((w) => w != null);

      const weekIndex = {};
      weekList.forEach((w, i) => {
        weekIndex[Number(w)] = i;
      });

      return (this.points || [])
        .map((pt, idx) => {
          const week = Number(pt.week);
          const score = clampScore(pt.score);
          if (!Number.isFinite(week) && !pt.label) return null;
          if (weekList.length && !(week in weekIndex) && !pt.label) return null;
          return {
            key: pt.key != null ? pt.key : `w-${week != null ? week : idx}`,
            label: pt.label || `Week ${week}`,
            score,
            tip: pt.tip || pt.name || '',
            _order: week in weekIndex ? weekIndex[week] : idx,
          };
        })
        .filter(Boolean)
        .sort((a, b) => a._order - b._order);
    },
    barHeight() {
      const n = Math.max(1, this.normalizedRows.length);
      const raw = (this.plot.height / n) * 0.42;
      return Math.max(6, Math.min(this.expanded ? 18 : 14, raw));
    },
    animatedBars() {
      const p = this.animProgress;
      const total = this.normalizedRows.length || 1;
      return this.normalizedRows.map((row, idx) => {
        const start = (idx / total) * 0.4;
        const local = Math.min(1, Math.max(0, (p - start) / 0.6));
        const eased = easeOutCubic(local);
        const fullW = this.scoreX(row.score) - this.plot.left;
        return {
          key: row.key,
          label: row.label,
          tip: row.tip,
          score: row.score,
          y: this.rowY(idx),
          width: Math.max(0, fullW * eased),
          opacity: 0.35 + 0.65 * eased,
        };
      });
    },
    animPassX() {
      const originX = this.plot.left;
      const t = easeOutCubic(Math.min(1, this.animProgress / 0.7));
      return originX + (this.passX - originX) * t;
    },
    animAvgX() {
      const originX = this.plot.left;
      const t = easeOutCubic(Math.min(1, Math.max(0, (this.animProgress - 0.08) / 0.7)));
      return originX + (this.scoreX(this.averageScore) - originX) * t;
    },
    lineOpacity() {
      return Math.min(1, this.animProgress * 1.4);
    },
  },
  watch: {
    rows: {
      deep: true,
      handler() {
        this.playIntro();
      },
    },
    points: {
      deep: true,
      handler() {
        this.playIntro();
      },
    },
    weeks() {
      this.playIntro();
    },
    passingScore() {
      this.playIntro();
    },
    averageScore() {
      this.playIntro();
    },
  },
  mounted() {
    this.playIntro();
  },
  beforeDestroy() {
    this.cancelAnim();
  },
  methods: {
    scoreX(score) {
      return this.plot.left + (clampScore(score) / 100) * this.plot.width;
    },
    rowY(index) {
      const n = this.normalizedRows.length;
      if (n <= 1) return (this.plot.top + this.plot.bottom) / 2;
      return this.plot.bottom - (index / (n - 1)) * this.plot.height;
    },
    cancelAnim() {
      if (this.animFrame) {
        cancelAnimationFrame(this.animFrame);
        this.animFrame = null;
      }
    },
    playIntro() {
      this.cancelAnim();
      this.clearHover();
      this.animReady = false;
      this.animProgress = 0;
      const token = ++this.animToken;
      const duration = 1100;
      const start = performance.now();

      const tick = (now) => {
        if (token !== this.animToken) return;
        const raw = Math.min(1, (now - start) / duration);
        this.animProgress = raw;
        if (raw < 1) {
          this.animFrame = requestAnimationFrame(tick);
        } else {
          this.animProgress = 1;
          this.animReady = true;
          this.animFrame = null;
        }
      };

      this.animFrame = requestAnimationFrame(tick);
    },
    onBarEnter(bar, idx, evt) {
      if (!this.expanded) return;
      const text = bar.tip || bar.label;
      if (!text) return;
      this.setHoverFromEvent(evt, text);
    },
    onBarMove(evt) {
      if (!this.hoverTip) return;
      this.setHoverFromEvent(evt, this.hoverTip.text);
    },
    setHoverFromEvent(evt, text) {
      const wrap = this.$el && this.$el.querySelector('.score-trend-plot-wrap');
      if (!wrap) return;
      const rect = wrap.getBoundingClientRect();
      this.hoverTip = {
        text,
        x: Math.min(rect.width - 12, Math.max(8, evt.clientX - rect.left + 12)),
        y: Math.max(8, evt.clientY - rect.top - 28),
      };
    },
    clearHover() {
      this.hoverTip = null;
    },
  },
};
</script>

<style scoped>
.score-trend-chart {
  width: 100%;
  max-width: 460px;
  margin: 0 auto;
}

.score-trend-chart.is-expanded {
  max-width: 720px;
}

.score-trend-title {
  margin: 0 0 4px;
  font-size: 13px;
  font-weight: 700;
  color: #231f20;
  text-align: center;
  font-family: Helvetica, Arial, sans-serif;
}

.score-trend-body {
  display: flex;
  align-items: stretch;
  gap: 2px;
}

.score-trend-y-label {
  writing-mode: vertical-rl;
  transform: rotate(180deg);
  font-size: 11px;
  font-weight: 600;
  color: #231f20;
  align-self: center;
  letter-spacing: 0.02em;
  flex-shrink: 0;
  padding: 0 1px;
}

.score-trend-plot-wrap {
  position: relative;
  flex: 1;
  min-width: 0;
}

.score-trend-svg {
  width: 100%;
  max-height: 220px;
  height: auto;
  display: block;
  overflow: visible;
}

.score-trend-chart.is-expanded .score-trend-svg {
  max-height: none;
  height: auto;
  min-height: 320px;
}

.zone-fail {
  fill: #ffb3b3;
  opacity: 0;
  animation: zone-fade 0.45s ease forwards;
}

.zone-pass {
  fill: #90ee90;
  opacity: 0;
  animation: zone-fade 0.45s ease 0.08s forwards;
}

@keyframes zone-fade {
  to { opacity: 1; }
}

.grid-h {
  stroke: #9e9e9e;
  stroke-width: 0.7;
  opacity: 0.5;
}

.axis-line {
  stroke: #616161;
  stroke-width: 1.2;
}

.axis-tick {
  stroke: #616161;
  stroke-width: 1;
}

.axis-label {
  fill: #231f20;
  font-size: 9px;
  font-family: Helvetica, Arial, sans-serif;
}

.axis-label--y {
  font-size: 9px;
  font-weight: 500;
}

.axis-label--x {
  font-size: 9px;
}

.axis-title {
  fill: #231f20;
  font-size: 11px;
  font-weight: 600;
  font-family: Helvetica, Arial, sans-serif;
}

.ref-line--pass {
  stroke: #2e7d32;
  stroke-width: 1.4;
  stroke-dasharray: 5 3;
}

.ref-line--avg {
  stroke: #212121;
  stroke-width: 1.4;
  stroke-dasharray: 5 3;
}

.data-bar {
  pointer-events: none;
}

.data-bar-hit {
  cursor: default;
}

.score-trend-chart.is-expanded .data-bar-hit {
  cursor: pointer;
}

.score-trend-tooltip {
  position: absolute;
  z-index: 5;
  transform: translate(-50%, -100%);
  background: #231f20;
  color: #fff;
  font-size: 12px;
  font-weight: 600;
  font-family: Helvetica, Arial, sans-serif;
  padding: 6px 10px;
  border-radius: 6px;
  white-space: nowrap;
  max-width: 220px;
  overflow: hidden;
  text-overflow: ellipsis;
  pointer-events: none;
  box-shadow: 0 4px 14px rgba(0, 0, 0, 0.22);
}

.score-trend-tooltip::after {
  content: '';
  position: absolute;
  left: 50%;
  bottom: -5px;
  transform: translateX(-50%);
  border: 5px solid transparent;
  border-top-color: #231f20;
  border-bottom-width: 0;
}

.score-trend-legend {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 8px 16px;
  margin-top: 6px;
  padding: 2px 4px 0;
}

.legend-item {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 11px;
  font-weight: 600;
  font-family: Helvetica, Arial, sans-serif;
}

.legend-item--pass {
  color: #2e7d32;
}

.legend-item--avg {
  color: #212121;
}

.legend-dash {
  display: inline-block;
  width: 22px;
  height: 0;
  border-top-width: 2px;
  border-top-style: dashed;
  flex-shrink: 0;
}

.legend-dash--pass {
  border-top-color: #2e7d32;
}

.legend-dash--avg {
  border-top-color: #212121;
}

@media (max-width: 640px) {
  .score-trend-chart {
    max-width: 100%;
  }

  .score-trend-legend {
    flex-direction: column;
    align-items: center;
    gap: 6px;
  }
}

@media (prefers-reduced-motion: reduce) {
  .zone-fail,
  .zone-pass {
    animation: none;
    opacity: 1;
  }
}
</style>
